// Libs
import React from "react";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";

// Components
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Box, InputAdornment, Menu, MenuItem, styled, TextField, type Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { CalendarButton } from "src/components/Button/CalendarButton";

interface SelectTimeProps {
    keys: string[];
    handleChange: (event: any) => void;
    value?: number | string;
    isCumulative?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            margin: theme.spacing(1),
        },
        input: {
            marginTop: 2,
            fontSize: 12,
            width: 140,
            cursor: "pointer",
        },

        button: {
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
    })
);

const StyledTextField = styled(TextField)(() => ({
    marginTop: 2,
    width: "168px",
    cursor: "pointer",
    border: "none",
    "& .MuiInputBase-root": {
        fontWeight: 400,
        height: "20px",
        width: "180px",
        border: "none",
    },
    "& .MuiInputBase-input": {
        fontSize: "12px",
        padding: "10px",
        height: "100%",
        boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
}));

export function SelectTime({
    keys,
    handleChange,
    value,
    isCumulative,
}: SelectTimeProps): JSX.Element {
    const classes = useStyles();
    const { translateCommon } = useTranslation();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const truncate = (value: string, maxLength: number) => {
        if (value.length > maxLength) {
            return `${value.slice(0, maxLength)}…`;
        } else {
            return value;
        }
    };
    return (
        <Box component="span">
            <CalendarButton
                ref={null}
                disableRipple
                className={classes.button}
                handleClick={handleClick}
            >
                <StyledTextField
                    slotProps={{
                        htmlInput: { className: classes.input },
                        input: {
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end" style={{ margin: 0 }}>
                                    <ArrowDropDown style={{ opacity: 0.7 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={truncate(
                        String(
                            translateCommon(`enums.compare-time-range.${value}`, {
                                displayAverage: isCumulative
                                    ? ` (${translateCommon(`average`)})`
                                    : "",
                            })
                        ),
                        20
                    )}
                />
            </CalendarButton>
            <Menu
                //cSpell: disable-next-line
                id="base-timerange"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                open={open}
                onClose={handleClose}
            >
                {keys.map((key, index) => {
                    return (
                        <MenuItem
                            onClick={() => handleChange(key)}
                            key={index}
                            value={key}
                            sx={{ fontSize: 12 }}
                        >
                            {translateCommon(`enums.compare-time-range.${key}`, {
                                displayAverage: isCumulative
                                    ? ` (${translateCommon(`average`)})`
                                    : "",
                            })}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
}
